import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Printer, ArrowLeft, Flower2 } from 'lucide-react';
import { useArchetypes, useResources } from '../context/ContentContext';
import { ArchetypeIllustration } from './ArchetypeIllustration';

export default function ResourcePage() {
  const { archetypeKey } = useParams();
  const key = (archetypeKey || '').toLowerCase();
  const archetypes = useArchetypes();
  const resources = useResources();
  const archetype = archetypes[key];
  const resource = resources[key];

  if (!archetype || !resource) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#6d655a' }}>
        <h1 className="font-heading text-[36px]" style={{ color: '#3D3830' }}>Toolkit not found</h1>
        <p className="font-body mt-3">
          Try one of: visionary, warrior, scholar, feeler, oracle, nurturer.
        </p>
        <Link to="/" className="inline-block mt-6 btn-ghost">Back to quiz</Link>
      </div>
    );
  }

  const handlePrint = () => {
    if (typeof window !== 'undefined') window.print();
  };

  return (
    <article
      data-testid="resource-page"
      className="resource-page mx-auto w-full max-w-[780px] px-5 sm:px-10 pt-10 pb-20"
    >
      {/* Screen-only top bar */}
      <div className="screen-only flex items-center justify-between mb-8 flex-wrap gap-3" data-testid="resource-topbar">
        <Link to="/" className="btn-ghost inline-flex items-center gap-2">
          <ArrowLeft size={14} strokeWidth={1.6} /> Back to quiz
        </Link>
        <button
          data-testid="resource-print-button"
          onClick={handlePrint}
          className="btn-primary"
        >
          <Printer size={14} strokeWidth={1.6} /> Save or print as PDF
        </button>
      </div>

      {/* Masthead */}
      <header className="text-center mb-10">
        <div className="mx-auto mb-5 inline-flex items-center justify-center" style={{ color: '#3D3830' }}>
          <ArchetypeIllustration archetypeKey={key} size={96} />
        </div>
        <p className="section-label mb-3">Your companion toolkit</p>
        <h1
          className="font-heading italic leading-[1.08] text-[34px] sm:text-[42px]"
          style={{ color: '#3D3830', fontWeight: 500 }}
          data-testid="resource-title"
        >
          {resource.title}
        </h1>
        <p
          className="font-body italic mt-3 text-[19px] sm:text-[22px]"
          style={{ color: 'rgba(61, 56, 48, 0.78)' }}
        >
          {resource.subtitle}
        </p>
      </header>

      {/* Intro */}
      <p
        className="font-body text-[16px] sm:text-[17px] leading-[1.75] mb-12 text-center mx-auto"
        style={{ color: '#3D3830', maxWidth: 640 }}
        data-testid="resource-intro"
      >
        {resource.intro}
      </p>

      {/* Sections */}
      <div className="flex flex-col gap-12">
        {resource.sections.map((s, i) => (
          <section key={i} data-testid={`resource-section-${i}`} className="resource-section">
            <div className="section-label mb-3">{s.label}</div>
            <p
              className="font-body text-[17px] leading-[1.7]"
              style={{ color: '#3D3830' }}
              dangerouslySetInnerHTML={{ __html: s.body }}
            />
            <ul className="mt-5 flex flex-col gap-3" data-testid={`resource-prompts-${i}`}>
              {s.prompts.map((p, j) => (
                <li
                  key={j}
                  className="rounded-xl px-4 py-3 font-body text-[16px] leading-[1.6]"
                  style={{
                    backgroundColor: 'rgba(184, 167, 143, 0.28)',
                    border: '1px solid rgba(184, 167, 143, 0.45)',
                    color: '#3D3830',
                  }}
                  dangerouslySetInnerHTML={{ __html: p }}
                />
              ))}
            </ul>
            {s.note && (
              <p
                className="font-heading italic mt-5 text-[16px] leading-[1.55]"
                style={{ color: 'rgba(61, 56, 48, 0.78)' }}
                dangerouslySetInnerHTML={{ __html: s.note }}
              />
            )}
          </section>
        ))}
      </div>

      {/* Closing */}
      <div
        className="mt-14 rounded-2xl px-6 sm:px-10 py-9 text-center"
        style={{ backgroundColor: '#3D3830', color: '#E8E0D5' }}
        data-testid="resource-closing"
      >
        <div className="font-body text-[11px] mb-3" style={{ letterSpacing: '0.28em', textTransform: 'uppercase', opacity: 0.75, fontStyle: 'normal' }}>
          Carry this with you
        </div>
        <p
          className="font-heading italic text-[22px] sm:text-[26px] leading-[1.4]"
          dangerouslySetInnerHTML={{ __html: `&ldquo;${resource.closing}&rdquo;` }}
        />
      </div>

      {/* Footer attribution */}
      <footer className="mt-12 text-center">
        <Flower2 size={20} strokeWidth={1.4} style={{ color: '#3D3830' }} className="mx-auto mb-2" />
        <p className="font-heading italic text-[15px]" style={{ color: '#6d655a' }}>
          Made for you by Kara Mears, Midwife (CNM) &amp; Mum
        </p>
        <a
          href="https://www.karamears.com"
          target="_top"
          rel="noopener noreferrer"
          className="font-body text-[12px] inline-block mt-2"
          style={{ color: '#3D3830', textDecoration: 'none', letterSpacing: '0.18em', textTransform: 'uppercase' }}
        >
          karamears.com
        </a>
      </footer>
    </article>
  );
}
